import { Component, computed, effect, inject, signal } from '@angular/core';
import { LvrDocumentEditor } from './document-editor';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import {
	ClientDocumentsResponse,
	DOCUMENT_CATEGORY,
	DocumentFile,
	DocumentNavItem,
} from '../../../../api/app-types/document.app';
import { buildDocumentNavItems } from '../../../../shared/helpers/documents.func';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { LvrDocumentDeleteDialog } from './document-delete-dialog';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';

@Component({
	selector: 'lvr-document-viewer',
	imports: [LvrDocumentEditor, TranslocoPipe],
	template: `
		@if (document(); as doc) {
			<lvr-document-editor
				[backLink]="backLink()"
				[document]="doc!"
				[documents]="navItems()"
				[canDeleteByPermission]="canDeleteDocumentByPermission()"
				(navigateToDocument)="onNavigateToDocument($event)"
				(deleteClicked)="openDialog()"
				(downloadClicked)="onDownload()"
				[isDownloading]="isDownloading()"
			/>
		} @else {
			<p>{{ 'client.documents.no_documents' | transloco }}</p>
		}
	`,
})
export class LvrDocumentViewer extends LvrRedirectNav {
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly documents = this.resolveDataAsSignal<ClientDocumentsResponse>('documents');
	private readonly clientId = this.resolveRouteParamAsSignal('id');
	private readonly contactId = this.resolveRouteParamAsSignal('contactId');
	public readonly document = this.resolveDataAsSignal<DocumentFile>('document');
	private readonly navItemsState = signal<DocumentNavItem[]>([]);
	public readonly isDownloading = signal<boolean>(false);
	private readonly canDeleteGeneralDocuments = hasPermission(this.session.permissions, 'Documents.General.Delete');
	private readonly canDeleteContactDocuments = hasPermission(this.session.permissions, 'Documents.Contact.Delete');
	private readonly canDeleteAssignmentDocuments = hasPermission(
		this.session.permissions,
		'Documents.Assignment.Delete',
	);
	private readonly canDeleteDisabilityReports = hasPermission(
		this.session.permissions,
		'Documents.DisabilityReport.Delete',
	);
	private readonly canDeleteDisabilityKinds = hasPermission(
		this.session.permissions,
		'Documents.DisabilityKind.Delete',
	);
	private readonly canDeleteReports = hasPermission(this.session.permissions, 'Reports.Delete');
	public readonly backLink = computed(() => `/desk/client/${this.clientId()}/documents`);
	public readonly navItems = computed(() => this.navItemsState());
	public readonly canDeleteDocumentByPermission = computed(() => {
		const category = this.navItems().find((x) => x.file.id === this.document()?.id)?.category;
		switch (category) {
			case DOCUMENT_CATEGORY.CONTACT:
				return this.canDeleteContactDocuments();
			case DOCUMENT_CATEGORY.ASSIGNMENT:
				return this.canDeleteAssignmentDocuments();
			case DOCUMENT_CATEGORY.DISABILITY_REPORT:
				return this.canDeleteDisabilityReports();
			case DOCUMENT_CATEGORY.DISABILITY_KIND:
				return this.canDeleteDisabilityKinds();
			case DOCUMENT_CATEGORY.REPORT:
				return this.canDeleteReports();
			case DOCUMENT_CATEGORY.UNKNOWN:
			default:
				return this.canDeleteGeneralDocuments();
		}
	});

	public constructor() {
		super();
		const state = (this.router.currentNavigation()?.extras.state as { navItems?: DocumentNavItem[] }) ?? {};
		this.navItemsState.set(state.navItems ?? []);

		// Fallback if refresh
		effect(() => {
			if (this.navItemsState().length) return;

			const docId = this.document()?.id;
			const entries = this.documents()?.entries?.entities ?? [];
			if (!docId || !entries.length) return;

			const entry = entries.find((e) => (e.externalFileReference ?? []).some((f) => f.id === docId));
			if (!entry) return;

			this.navItemsState.set(buildDocumentNavItems([entry]));
		});
	}

	public onNavigateToDocument(documentId: string) {
		let route = ['/desk/client', this.clientId(), 'documents', documentId];

		if (!this.clientId()) {
			route = ['/desk/network-contact', this.contactId(), 'documents', documentId];
		}

		this.navigateAfterFinished(route, {
			// Use replaceUrl to avoid adding carousel navigation to browser history
			replaceUrl: true,
			state: { navItems: this.navItems() },
		});
	}

	public onDownload() {
		this.isDownloading.set(true);
		try {
			const doc = this.document();
			if (!doc) return;
			if (!doc.location.startsWith('https://')) return;

			const link = document.createElement('a');
			link.href = doc.location;
			link.download = doc.name;
			link.target = '_blank';
			link.click();

			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.file_downloaded')),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.file_download_failed')),
			);
		} finally {
			this.isDownloading.set(false);
		}
	}

	public async openDialog() {
		if (!this.canDeleteDocumentByPermission()) return;
		const result = await lastValueFrom(
			this.dialog
				.open(LvrDocumentDeleteDialog, {
					width: '400px',
					data: { document: this.document() },
				})
				.afterClosed(),
		);
		if (!result) return;

		const returnUrl = history.state?.returnUrl;

		if (returnUrl) {
			this.router.navigateByUrl(returnUrl);
		} else {
			this.router.navigate(['../'], { relativeTo: this.route });
		}
	}
}
